import { useState, KeyboardEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Icon from 'components/shared/Icon'
import IconWithText from 'components/shared/IconWithText'
import { Image } from 'components/shared/Image'
import { Collapsible } from 'components/shared/Collapsible'
import {
    StyledColumns,
    StyledGuestsBadge,
    StyledTop,
    StyledTrigger,
} from './BungalowInfoBanner.styles'
import { BungalowInfoBannerProps } from './BungalowInfoBanner.types'

import iconCheck from 'assets/icons/check.svg'
import iconStar from 'assets/icons/star-gold.svg'
import { useResolutions } from 'store/resolutions'

export const BungalowInfoBanner = ({
    id,
    name,
    description,
    image,
    maxGuests,
    equipment,
    equipmentPremium,
    isNew,
    isPremium,
}: BungalowInfoBannerProps) => {
    const { formatMessage } = useIntl()
    const { isMobile } = useResolutions()

    const [isExpanded, setIsExpanend] = useState(false)

    const handleClick = () => setIsExpanend((prev) => !prev)
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) =>
        event.key === 'Enter' && handleClick()

    return (
        <Box
            pt={40}
            px={[24, 40]}
            pb={32}
            gap={16}
            bgColor="grayscale_0"
            borderRadius={9}
            isFullWidth
        >
            <StyledTop direction="row" align="center" gap={24} isFullWidth>
                {!isMobile && (
                    <Image
                        width={96}
                        height={96}
                        borderRadius={8}
                        src={image}
                        alt="overview of beds in bungalow"
                    />
                )}
                <Box justify="center" gap={[16, 8]} width={['100%', '65%']}>
                    <Box direction={['column-reverse', 'row']} gap={[4, 8]}>
                        <Text variant="heading4" color="secondary_700">
                            {name}
                        </Text>

                        {/* 'NEW' badge */}
                        {isNew && (
                            <Box px={8} bgColor="error_bg" borderRadius={4}>
                                <Text
                                    variant="bodyExtraSmall"
                                    color="error"
                                    isUppercase
                                >
                                    <FormattedMessage
                                        id="global.badge.new"
                                        defaultMessage="_"
                                    />
                                </Text>
                            </Box>
                        )}
                    </Box>
                    <Text variant="bodySmall" color="grayscale_800">
                        {description}
                    </Text>
                </Box>
                <StyledGuestsBadge isPremium={isPremium}>
                    <Text
                        variant="bodyExtraSmall"
                        color={isPremium ? 'primary_500' : 'secondary_500'}
                        align="center"
                    >
                        <FormattedMessage
                            id="accommodation.bungalow_modal.guests_limit"
                            defaultMessage="_"
                            values={{
                                guests_amount: maxGuests,
                                strong: (chunks: string[]) => (
                                    <Text
                                        as="strong"
                                        variant="heading3"
                                        color={
                                            isPremium
                                                ? 'primary_500'
                                                : 'secondary_500'
                                        }
                                    >
                                        {chunks}
                                    </Text>
                                ),
                            }}
                        />
                    </Text>
                </StyledGuestsBadge>
            </StyledTop>

            {/* collapsible details */}
            <Box isFullWidth>
                <StyledTrigger
                    direction="row"
                    justify="space-between"
                    align="center"
                    pr={[0, 6]}
                    isFullWidth
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                    data-testid={`${id}-equipment-trigger`}
                >
                    <Text variant="bodyStrong" color="secondary_500">
                        <FormattedMessage
                            id="accommodation.bungalow_modal.equipment_section_caption"
                            defaultMessage="_"
                        />
                    </Text>
                    <Icon
                        variant="chevron"
                        color="secondary_500"
                        size={32}
                        rotation={isExpanded ? -180 : 0}
                    />
                </StyledTrigger>
                <Collapsible isExpanded={isExpanded}>
                    <Box
                        mt={[8, 12]}
                        gap={[16, 12]}
                        direction={['column-reverse', 'column']}
                    >
                        <StyledColumns>
                            {equipment.map(({ name, isOnDemand }) => (
                                <IconWithText
                                    textHighlighted={formatMessage(name)}
                                    text={
                                        isOnDemand
                                            ? formatMessage({
                                                  id: 'accommodation.bungalow_modal.equipment_on_demand',
                                                  defaultMessage: '_',
                                              })
                                            : undefined
                                    }
                                    fontVariant="bodySmall"
                                    textHighlightedColor="grayscale_900"
                                    textColor="grayscale_800"
                                    icon={iconCheck}
                                    gap={12}
                                    iconWidth={16}
                                    align="flex-start"
                                    hasFullLineTexts
                                    key={name.id}
                                />
                            ))}
                        </StyledColumns>
                        {!!equipmentPremium?.length && (
                            <StyledColumns>
                                {equipmentPremium.map(
                                    ({ name, isOnDemand }) => (
                                        <IconWithText
                                            textHighlighted={formatMessage(
                                                name
                                            )}
                                            text={
                                                isOnDemand
                                                    ? formatMessage({
                                                          id: 'accommodation.bungalow_modal.equipment_on_demand',
                                                          defaultMessage: '_',
                                                      })
                                                    : undefined
                                            }
                                            fontVariant="bodySmall"
                                            textHighlightedColor="grayscale_900"
                                            textColor="grayscale_800"
                                            icon={iconStar}
                                            gap={12}
                                            iconWidth={16}
                                            align="flex-start"
                                            hasFullLineTexts
                                            key={name.id}
                                        />
                                    )
                                )}
                            </StyledColumns>
                        )}
                    </Box>
                </Collapsible>
            </Box>
        </Box>
    )
}

BungalowInfoBanner.displayName = 'BungalowInfoBanner'
